import React, { useEffect, useState } from 'react';
import { Modal, Select, DatePicker, Table, Input, Tooltip, message } from 'antd';
import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import FloatLabel from '../../components/FloatLabel/floatLabel';
import Button from '../../components/Buttons/Button';

const MAX_DESCRIPTION_LENGTH = 250;
const scheduleDescriptionOptions = [
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Milestone', value: 'MILESTONE' },
  { label: 'Custom', value: 'CUSTOM' },
];
const percentageAvailableOptions = [{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }];

interface Installment {
  id?: any;
  sequenceNo: number;
  periodLabel: string;
  description: string;
  invoiceDate: any;
  percentage?: number;
  amount?: number;
  locked?: boolean;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  onSave: (schedule: any) => void;
  totalContractValue: any;
  currency: string;
  initialSchedule?: any;
}

const isPastDate = (date: any) => !!date && dayjs(date).isBefore(dayjs(), 'day');

const handleAmountKeyDown = (e: any) => {
  if (e.ctrlKey && (e.key === 'c' || e.key === 'v')) return;
  if (!(/[0-9]/.test(e.key) || ['.', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key))) {
    e.preventDefault();
  }
};

const InvoiceScheduleModal: React.FC<Props> = ({ open, onCancel, onSave, totalContractValue, currency, initialSchedule }) => {
  const [scheduleId, setScheduleId] = useState<any>(undefined);
  const [scheduleDescription, setScheduleDescription] = useState<string>('');
  const [percentageAvailable, setPercentageAvailable] = useState<string>('No');
  const [startMonth, setStartMonth] = useState<any>(null);
  const [endMonth, setEndMonth] = useState<any>(null);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [rowDescription, setRowDescription] = useState<string>('');
  const [rowInvoiceDate, setRowInvoiceDate] = useState<any>(null);
  const [rowPercentage, setRowPercentage] = useState<any>(undefined);
  const [rowAmount, setRowAmount] = useState<any>(undefined);

  useEffect(() => {
    if (open) {
      if (initialSchedule && Object.keys(initialSchedule).length > 0) {
        setScheduleId(initialSchedule.id);
        setScheduleDescription(initialSchedule.scheduleDescription || '');
        setPercentageAvailable(initialSchedule.percentageAvailable ? 'Yes' : 'No');
        setStartMonth(initialSchedule.startMonth ? dayjs(initialSchedule.startMonth) : null);
        setEndMonth(initialSchedule.endMonth ? dayjs(initialSchedule.endMonth) : null);
        setInstallments((initialSchedule.installments || []).map((item: any) => ({
          ...item,
          invoiceDate: item.invoiceDate ? dayjs(item.invoiceDate) : null,
          locked: isPastDate(item.invoiceDate),
        })));
      } else {
        resetState();
      }
    }
  }, [open, initialSchedule]);

  const resetState = () => {
    setScheduleId(undefined);
    setScheduleDescription('');
    setPercentageAvailable('No');
    setStartMonth(null);
    setEndMonth(null);
    setInstallments([]);
    setRowDescription('');
    setRowInvoiceDate(null);
    setRowPercentage(undefined);
    setRowAmount(undefined);
  };

  const handleScheduleDescriptionChange = (value: string) => {
    if (value !== scheduleDescription && installments.some((item) => item.locked)) {
      // past invoices are non-editable; the schedule type can't be swapped without losing that history
      message.error('This schedule has invoice(s) with a past date and cannot be changed. Delete the existing schedule and create a new one to change the Schedule Description.');
      return;
    }
    setScheduleDescription(value);
    setInstallments([]);
    setStartMonth(null);
    setEndMonth(null);
    setRowDescription('');
    setRowInvoiceDate(null);
    setRowPercentage(undefined);
    setRowAmount(percentageAvailable === 'No' ? totalContractValue : undefined);
  };

  const handlePercentageAvailableChange = (value: string) => {
    setPercentageAvailable(value);
    setRowAmount(value === 'No' ? totalContractValue : undefined);
    setRowPercentage(undefined);
    const tcv = parseFloat(totalContractValue) || 0;
    setInstallments((prev) => prev.map((item) => {
      if (value === 'Yes') {
        const percentage = tcv > 0 ? parseFloat((((parseFloat(item.amount as any) || 0) / tcv) * 100).toFixed(2)) : item.percentage;
        const amount = tcv > 0 ? calcAmountFromPercentage(percentage) : item.amount;
        return { ...item, percentage, amount };
      }
      return { ...item, percentage: undefined };
    }));
  };

  const calcAmountFromPercentage = (percentage: any) => {
    const tcv = parseFloat(totalContractValue) || 0;
    const pct = parseFloat(percentage) || 0;
    return parseFloat(((pct / 100) * tcv).toFixed(2));
  };

  // split a total proportionally across the given weights, putting any rounding remainder on the last entry
  const distributeProportionally = (total: number, weights: number[]) => {
    if (weights.length === 0) return [];
    const sumWeights = weights.reduce((s, w) => s + w, 0);
    const effectiveWeights = sumWeights > 0 ? weights : weights.map(() => 1);
    const effectiveSum = effectiveWeights.reduce((s, w) => s + w, 0);
    const values = effectiveWeights.map((w) => parseFloat(((w / effectiveSum) * total).toFixed(2)));
    const roundedSum = values.reduce((s, v) => s + v, 0);
    values[values.length - 1] = parseFloat((values[values.length - 1] + (total - roundedSum)).toFixed(2));
    return values;
  };

  const deleteMonthlyRow = (index: number) => {
    const target = installments[index];
    if (target.locked) return;
    const remaining = installments.filter((_, i) => i !== index);
    if (percentageAvailable !== 'Yes') {
      setInstallments(remaining);
      return;
    }
    const tcv = parseFloat(totalContractValue) || 0;
    const pastItems = remaining.filter((item) => item.locked);
    const futureItems = remaining.filter((item) => !item.locked);

    const pastAmounts = pastItems.map((item) => parseFloat(item.amount as any) || 0);
    const sumPastAmount = pastAmounts.reduce((s, a) => s + a, 0);
    const pastPercentages = pastAmounts.map((amount) => (tcv > 0 ? parseFloat(((amount / tcv) * 100).toFixed(2)) : 0));
    const sumPastPercentage = pastPercentages.reduce((s, p) => s + p, 0);

    const remainingAmount = parseFloat((tcv - sumPastAmount).toFixed(2));
    const remainingPercentage = parseFloat((100 - sumPastPercentage).toFixed(2));
    const futureWeights = futureItems.map((item) => parseFloat(item.percentage as any) || 0);
    const futureAmounts = distributeProportionally(remainingAmount, futureWeights);
    const futurePercentages = distributeProportionally(remainingPercentage, futureWeights);

    let pastIdx = 0;
    let futureIdx = 0;
    const updated = remaining.map((item) => {
      if (item.locked) {
        const result = { ...item, amount: pastAmounts[pastIdx], percentage: pastPercentages[pastIdx] };
        pastIdx++;
        return result;
      }
      const result = { ...item, amount: futureAmounts[futureIdx], percentage: futurePercentages[futureIdx] };
      futureIdx++;
      return result;
    });
    setInstallments(updated);
  };

  const handleGenerateMonths = () => {
    if (!startMonth || !endMonth) return;
    if (endMonth.isBefore(startMonth, 'month')) {
      message.error('End Month cannot be before Start Month.');
      return;
    }
    const periodLabels: string[] = [];
    let cursor = startMonth.startOf('month');
    while (cursor.year() < endMonth.year() || (cursor.year() === endMonth.year() && cursor.month() <= endMonth.month())) {
      periodLabels.push(cursor.format('MMM-YYYY'));
      cursor = cursor.add(1, 'month');
    }

    // locked (past-dated) months are never regenerated/dropped, regardless of the selected range
    const lockedItems = installments.filter((item) => item.locked);
    const lockedLabels = new Set(lockedItems.map((item) => item.periodLabel));
    const newLabels = periodLabels.filter((label) => !lockedLabels.has(label));

    const tcv = parseFloat(totalContractValue) || 0;
    const sumLockedAmount = lockedItems.reduce((sum, item) => sum + (parseFloat(item.amount as any) || 0), 0);
    const remainingAmount = parseFloat((tcv - sumLockedAmount).toFixed(2));
    // split the remaining contract value evenly across the new months, putting any rounding remainder on the last month
    const equalAmount = newLabels.length > 0 ? parseFloat((remainingAmount / newLabels.length).toFixed(2)) : 0;

    const generatedMonths: Installment[] = newLabels.map((periodLabel, index) => {
      const existing = installments.find((item) => item.periodLabel === periodLabel && !item.locked);
      if (existing) return existing;
      const monthCursor = startMonth.startOf('month').add(periodLabels.indexOf(periodLabel), 'month');
      const amount = percentageAvailable === 'No'
        ? (index === newLabels.length - 1 ? parseFloat((remainingAmount - equalAmount * (newLabels.length - 1)).toFixed(2)) : equalAmount)
        : undefined;
      return {
        sequenceNo: 0,
        periodLabel,
        description: `Monthly invoice — ${monthCursor.format('MMM YYYY')}`,
        invoiceDate: monthCursor.date(5),
        percentage: undefined,
        amount,
        locked: false,
      };
    });

    const months = [...lockedItems, ...generatedMonths]
      .sort((a, b) => dayjs(a.invoiceDate).valueOf() - dayjs(b.invoiceDate).valueOf())
      .map((item, index) => ({ ...item, sequenceNo: index + 1 }));
    setInstallments(months);
  };


  const updateInstallment = (index: number, changes: Partial<Installment>) => {
    setInstallments((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...changes };
      if (percentageAvailable === 'Yes' && changes.percentage !== undefined) {
        updated[index].amount = calcAmountFromPercentage(changes.percentage);
      }
      return updated;
    });
  };

  const totalAllocated = percentageAvailable === 'Yes'
    ? installments.reduce((sum, item) => sum + (parseFloat(item.percentage as any) || 0), 0)
    : installments.reduce((sum, item) => sum + (parseFloat(item.amount as any) || 0), 0);

  const addManualRow = () => {
    if (!rowDescription.trim()) {
      message.error(`${scheduleDescription === 'MILESTONE' ? 'Milestone Description' : 'Description'} is required.`);
      return;
    }
    if (!rowInvoiceDate) {
      message.error('Invoice Date is required.');
      return;
    }
    if (percentageAvailable === 'Yes') {
      const pct = parseFloat(rowPercentage);
      if (rowPercentage === undefined || rowPercentage === null || rowPercentage === '' || isNaN(pct) || pct <= 0) {
        message.error('Percentage is required and must be greater than 0.');
        return;
      }
    }
    const amount = percentageAvailable === 'Yes' ? calcAmountFromPercentage(rowPercentage) : (rowAmount !== undefined ? parseFloat(rowAmount) : undefined);
    const newRow: Installment = {
      sequenceNo: installments.length + 1,
      periodLabel: rowDescription,
      description: rowDescription,
      invoiceDate: rowInvoiceDate,
      percentage: percentageAvailable === 'Yes' ? parseFloat(rowPercentage) : undefined,
      amount,
      locked: false,
    };
    setInstallments([...installments, newRow]);
    setRowDescription('');
    setRowInvoiceDate(null);
    setRowPercentage(undefined);
    setRowAmount(undefined);
  };

  const deleteRow = (index: number) => {
    setInstallments(installments.filter((_, i) => i !== index));
  };

  const isSaveEnabled = () => {
    if (!scheduleDescription) return false;
    if (installments.length === 0) return false;
    if (scheduleDescription === 'MONTHLY' && (!startMonth || !endMonth)) return false;
    return true;
  };

  const handleSave = () => {
    if (!scheduleDescription) {
      message.error('Schedule Description is mandatory.');
      return;
    }
    if (installments.length === 0) {
      message.error('Please add at least one schedule entry.');
      return;
    }
    if (percentageAvailable === 'Yes') {
      const hasInvalidPercentage = installments.some((item) => {
        const pct = parseFloat(item.percentage as any);
        return item.percentage === undefined || item.percentage === null || (item.percentage as any) === '' || isNaN(pct) || pct <= 0;
      });
      if (hasInvalidPercentage) {
        message.error('Percentage is required and must be greater than 0 for every entry.');
        return;
      }
    }
    if (percentageAvailable === 'Yes' && Math.round(totalAllocated) !== 100) {
      message.error('Total allocated percentage must be equal to 100% of Total Contract Value.');
      return;
    }
    const payload = {
      ...(scheduleId ? { id: scheduleId } : {}),
      scheduleDescription,
      percentageAvailable: percentageAvailable === 'Yes',
      totalContractValue: parseFloat(totalContractValue) || 0,
      startMonth: startMonth ? startMonth.format('YYYY-MM-DD') : null,
      endMonth: endMonth ? endMonth.format('YYYY-MM-DD') : null,
      totalAllocatedPercentage: percentageAvailable === 'Yes' ? totalAllocated : null,
      totalAllocatedAmount: installments.reduce((sum, item) => sum + (parseFloat(item.amount as any) || 0), 0),
      installments: installments.map((item, index) => ({
        ...(item.id !== undefined && item.id !== null ? { id: item.id } : {}),
        sequenceNo: index + 1,
        periodLabel: item.periodLabel,
        description: item.description,
        invoiceDate: item.invoiceDate ? dayjs(item.invoiceDate).format('YYYY-MM-DD') : null,
        percentage: item.percentage,
        amount: item.amount,
        locked: item.locked || false,
      })),
    };
    onSave(payload);
  };

  const monthlyColumns: any[] = [
    { title: 'Month', dataIndex: 'description', key: 'description' },
    {
      title: 'Invoice Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      render: (value: any, record: Installment, index: number) => (
        record.locked ? <span>{dayjs(value).format('YYYY-MM-DD')}</span> : (
          <DatePicker
            value={value}
            format="DD-MM-YYYY"
            allowClear={false}
            onChange={(date) => updateInstallment(index, { invoiceDate: date })}
          />
        )
      ),
    },
    ...(percentageAvailable === 'Yes' ? [{
      title: 'Percentage (%)',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (value: any, record: Installment, index: number) => (
        record.locked ? <span>{value ?? '—'}</span> : (
          <Input value={value} onKeyDown={handleAmountKeyDown} onChange={(e) => updateInstallment(index, { percentage: e.target.value as any })} />
        )
      ),
    }] : []),
    {
      title: `Amount (${currency || 'USD'})`,
      dataIndex: 'amount',
      key: 'amount',
      render: (value: any, record: Installment, index: number) => (
        percentageAvailable === 'Yes' || record.locked ? <span>{value ?? '—'}</span> : (
          <Input
            value={value}
            onKeyDown={handleAmountKeyDown}
            onChange={(e) => updateInstallment(index, { amount: e.target.value as any })}
          />
        )
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: Installment, index: number) => (
        record.locked ? null : (
          <Tooltip title="Delete this month">
            <DeleteOutlined style={{ color: '#d4380d', cursor: 'pointer' }} onClick={() => deleteMonthlyRow(index)} />
          </Tooltip>
        )
      ),
    },
  ];

  const manualColumns: any[] = [
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Invoice Date', dataIndex: 'invoiceDate', key: 'invoiceDate', render: (value: any) => value ? dayjs(value).format('YYYY-MM-DD') : '—' },
    ...(percentageAvailable === 'Yes' ? [{ title: 'Percentage (%)', dataIndex: 'percentage', key: 'percentage', render: (value: any) => value ?? '—' }] : []),
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (value: any) => value ?? '—' },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: Installment, index: number) => (
        <Tooltip title={record.locked ? 'This entry has a past invoice date and cannot be deleted here. Delete the entire schedule to make changes.' : 'Delete'}>
          <DeleteOutlined style={{ color: record.locked ? '#00000040' : '#d4380d', cursor: record.locked ? 'not-allowed' : 'pointer' }} onClick={() => !record.locked && deleteRow(index)} />
        </Tooltip>
      ),
    },
  ];

  const hasLockedRows = installments.some((item) => item.locked);

  return (
    <Modal
      title="Invoice Schedule"
      open={open}
      onCancel={onCancel}
      width={scheduleDescription === 'MONTHLY' && installments.length > 0 ? 900 : 700}
      footer={[
        <Button key="cancel" type="cancel" label="CANCEL" onClick={onCancel} />,
        <Button key="save" type="submit" label="SAVE SCHEDULE" onClick={handleSave} disabled={!isSaveEnabled()} />,
      ]}
    >
      {hasLockedRows && (
        <div style={{ color: '#d4380d', marginBottom: '10px' }}>
          Some entries have invoice dates that have already passed and cannot be edited. To change them, delete this schedule from the Work Order and create a new one.
        </div>
      )}
      <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ flex: 1, minWidth: '200px', display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
          <div style={{ flex: 1 }}>
            <FloatLabel label="Schedule Description *" value={scheduleDescription}>
              <Select
                style={{ width: '100%', height: 58 }}
                value={scheduleDescription || undefined}
                onChange={handleScheduleDescriptionChange}
                options={scheduleDescriptionOptions}
              />
            </FloatLabel>
          </div>
          <Tooltip title={
            <>
              <p><b>Monthly:</b> Recurring invoices generated for every month in a selected date range.</p>
              <p><b>Milestone:</b> Invoice raised on completion of specific project milestones.</p>
              <p><b>Custom:</b> Manually defined invoice entries with custom description and date.</p>
            </>
          }>
            <QuestionCircleOutlined style={{ color: '#034EA2', marginBottom: '18px' }} />
          </Tooltip>
        </div>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <FloatLabel label="Percentage Available *" value={percentageAvailable}>
            <Select
              style={{ width: '100%', height: 58 }}
              value={percentageAvailable}
              onChange={handlePercentageAvailableChange}
              options={percentageAvailableOptions}
            />
          </FloatLabel>
        </div>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <FloatLabel label={`Total Contract Value (${currency || 'USD'})`} value={totalContractValue}>
            <Input value={totalContractValue} disabled style={{ width: '100%', height: 58, paddingTop: '15px' }} />
          </FloatLabel>
        </div>
      </div>

      {scheduleDescription === 'MONTHLY' && (
        <>
          <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <FloatLabel label="Start Month *" value={startMonth}>
                <DatePicker picker="month" placeholder="" style={{ width: '100%', height: 58 }} value={startMonth} onChange={setStartMonth} />
              </FloatLabel>
            </div>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <FloatLabel label="End Month *" value={endMonth}>
                <DatePicker picker="month" placeholder="" style={{ width: '100%', height: 58 }} value={endMonth} onChange={setEndMonth} />
              </FloatLabel>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button type="submit" label="GENERATE MONTHS" width="180px" height="58px" onClick={handleGenerateMonths} disabled={!startMonth || !endMonth} />
            </div>
          </div>
          {installments.length > 0 && (
            <>
              <Table
                style={{ marginTop: '15px' }}
                pagination={false}
                rowKey={(record: Installment, index?: number) => `${record.description}-${index}`}
                columns={monthlyColumns}
                dataSource={installments}
              />
              <div style={{ marginTop: '10px', color: '#034EA2' }}>
                {percentageAvailable === 'Yes'
                  ? `Total allocated: ${totalAllocated.toFixed(2)}% of Total Contract Value`
                  : `Total allocated: ${totalAllocated.toFixed(2)} of Total Contract Value (${totalContractValue})`}
              </div>
            </>
          )}
        </>
      )}

      {(scheduleDescription === 'MILESTONE' || scheduleDescription === 'CUSTOM') && (
        <>
          <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <FloatLabel label={scheduleDescription === 'MILESTONE' ? 'Milestone Description *' : 'Description'} value={rowDescription}>
                <Input
                  value={rowDescription}
                  maxLength={scheduleDescription === 'MILESTONE' ? MAX_DESCRIPTION_LENGTH : undefined}
                  style={{ width: '100%', height: 58, paddingTop: '15px' }}
                  onChange={(e) => setRowDescription(e.target.value)}
                />
              </FloatLabel>
            </div>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <FloatLabel label="Invoice Date *" value={rowInvoiceDate}>
                <DatePicker placeholder="" style={{ width: '100%', height: 58 }} format="DD-MM-YYYY" value={rowInvoiceDate} onChange={setRowInvoiceDate} />
              </FloatLabel>
            </div>
            {percentageAvailable === 'Yes' ? (
              <div style={{ flex: 1, minWidth: '150px' }}>
                <FloatLabel label="Percentage (%) *" value={rowPercentage}>
                  <Input value={rowPercentage} onKeyDown={handleAmountKeyDown} style={{ width: '100%', height: 58 }} onChange={(e) => setRowPercentage(e.target.value)} />
                </FloatLabel>
              </div>
            ) : (
              <div style={{ flex: 1, minWidth: '150px' }}>
                <FloatLabel label={`Amount (${currency || 'USD'})`} value={rowAmount}>
                  <Input value={rowAmount} onKeyDown={handleAmountKeyDown} style={{ width: '100%', height: 58 }} onChange={(e) => setRowAmount(e.target.value)} />
                </FloatLabel>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button type="submit" label="ADD" onClick={addManualRow} />
            </div>
          </div>
          <Table
            style={{ marginTop: '15px' }}
            pagination={false}
            rowKey={(record: Installment, index?: number) => `${record.description}-${index}`}
            columns={manualColumns}
            dataSource={installments}
            locale={{ emptyText: 'No schedule added' }}
          />
        </>
      )}
    </Modal>
  );
};

export default InvoiceScheduleModal;
